import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { CourseListViewMode } from '../pages/courses-list/CoursesListView.consts';
import { CoursesListViewConsts } from '../pages/courses-list/CoursesListView.consts';

import * as asyncThunks from './async-thunks';
import type {
  AbortedRequestPayload,
  CourseModel,
  CoursesState,
  GetCoursesRejectedPayload,
  NewDesktopConnected
} from './types';
import {
  changeCourseValues,
  changeParticipantValues,
  getExistingCourse,
  setHasFreshStart
} from './utils';

import type { ParticipantModel } from 'Api';

const loadViewModeFromStorage = (): CourseListViewMode => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return CoursesListViewConsts.defaultViewMode;
    }

    localStorage.removeItem('courseSearchSettings');

    const stored = localStorage.getItem('courseListViewMode');
    if (stored) {
      return stored as CourseListViewMode;
    }
  } catch (error) {
    console.warn('Failed to load view mode from localStorage:', error);
  }

  return CoursesListViewConsts.defaultViewMode;
};

const saveViewModeToStorage = (viewMode: CourseListViewMode) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    localStorage.setItem('courseListViewMode', viewMode);
  } catch (error) {
    console.warn('Failed to save view mode to localStorage:', error);
  }
};

const initialState: CoursesState = {
  courses: [],
  areCoursesLoading: false,
  coursesError: null,
  availableTagsWithTranslations: [],
  areTagsLoading: false,

  courseListViewMode: loadViewModeFromStorage(),

  isCoursesLoaded: false,

  isCourseCreating: false,
  isCourseUpdating: false,
  isCourseForEditLoading: false,
  courseForEditError: null,
  isFreshStartCreating: false,

  isCreateCourseSuccess: false,
  isUpdateCourseSuccess: false,
  createCourseResult: null,
  updateCourseResult: null,

  standalones: [],
  areStandalonesLoading: false,
  freshStartServers: {},
  areFreshStartServersLoading: {},
  osTemplates: {},
  areOsTemplatesLoading: {}
};

const coursesSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    clearCourses(state) {
      state.courses = [];
      state.pagination = undefined;
      state.isCoursesLoaded = false;
      state.coursesError = null;
    },
    addParticipant(
      state,
      action: PayloadAction<{ participant: ParticipantModel; courseId: string }>
    ) {
      const course = getExistingCourse(state.courses, action.payload.courseId);

      if (course) {
        course.participants ??= [];
        course.participants.push({
          ...action.payload.participant,
          isDesktopStartPending: false,
          isDesktopStopPending: false,
          isDesktopDeletePending: false
        });
      }
    },
    newDesktopConnected(
      state,
      action: PayloadAction<{ model: NewDesktopConnected; courseId: string }>
    ) {
      const course = getExistingCourse(state.courses, action.payload.courseId);

      if (course) {
        const participant = course.participants?.find(x => x.id == action.payload.model.userId);
        if (participant) {
          participant.desktop = action.payload.model.desktop;
        }
      }
    },
    courseTrainerAllowedToPreview(
      state,
      action: PayloadAction<{ courseId: string; value: boolean }>
    ) {
      const course = getExistingCourse(state.courses, action.payload.courseId);

      if (course) {
        course.whetherTrainerAllowedPreview = action.payload.value;
      }
    },
    isFreshStartMachineReadyToClone(
      state,
      action: PayloadAction<{ courseId: string; value: boolean }>
    ) {
      const course = getExistingCourse(state.courses, action.payload.courseId);

      if (course) {
        course.isFreshStartMachineReadyToClone = action.payload.value;
      }
    },
    setCourseListViewMode(state, action: PayloadAction<CourseListViewMode>) {
      state.courseListViewMode = action.payload;
      saveViewModeToStorage(action.payload);
    },
    clearCreateCourseResult: state => {
      state.createCourseResult = null;
      state.isCreateCourseSuccess = false;
    },
    clearUpdateCourseResult: state => {
      state.updateCourseResult = null;
      state.isUpdateCourseSuccess = false;
    },
    clearCourseForEditError: state => {
      state.courseForEditError = null;
    },
    resetCoursesError: state => {
      state.coursesError = null;
    },
    setCoursesLoaded: (state, action: PayloadAction<boolean>) => {
      state.isCoursesLoaded = action.payload;
    },
    clearOsTemplatesError: (state, action: PayloadAction<{ serverName: string }>) => {
      const { serverName } = action.payload;
      if (state.osTemplatesErrors) {
        state.osTemplatesErrors[serverName] = false;
      }
    },
    resetCourseDataLoadedStates: (state, action: PayloadAction<{ courseId: string }>) => {
      const { courseId } = action.payload;
      const course = state.courses?.find(c => c.id === courseId);
      if (course) {
        course.isJoinCodeLoaded = false;
        course.isMyDesktopLoaded = false;
        course.isFreshStartDesktopLoaded = false;
        course.areParticipantsLoaded = false;
      }
    },
    resetDesktopLoadingStates: state => {
      if (state.courses) {
        state.courses.forEach(course => {
          course.isMyDesktopLoading = false;
          course.isTrainerDesktopLoading = false;
          course.isFreshStartDesktopLoading = false;
          course.areParticipantsLoading = false;
        });
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(asyncThunks.getCourses.pending, state => {
        state.areCoursesLoading = true;
      })
      .addCase(asyncThunks.getCourses.fulfilled, (state, action) => {
        const apiData = action.payload;

        if (apiData?.courses) {
          const courses = apiData.courses.map((course: any) => ({
            ...course,
            machineIframeSize: course.machineIframeSize ?? course.MachineIframeSize ?? 4,
            joinCode: course.joinCode ?? course.JoinCode ?? '',
            maxParticipants: course.maxParticipants ?? 0,
            expectedParticipants: course.expectedParticipants ?? course.maxParticipants ?? 0,
            participantCount: course.participantCount ?? 0,
            activeParticipantCount: course.activeParticipantCount ?? 0
          })) as Array<CourseModel>;

          if (apiData.isLoadingMore) {
            state.courses = [...state.courses, ...courses];
          } else {
            state.courses = courses;
          }

          state.pagination = apiData.pagination;
        }

        state.areCoursesLoading = false;
        state.coursesError = null;
        state.isCoursesLoaded = true;
      })
      .addCase(asyncThunks.getCourses.rejected, (state, action) => {
        const payload = action.payload as GetCoursesRejectedPayload | undefined;
        const isAborted =
          payload != null && 'type' in payload && payload.type === 'aborted';
        if (isAborted) {
          return;
        }

        state.areCoursesLoading = false;
        const errorMessage =
          (payload != null && 'message' in payload ? payload.message : null) ??
          action.error?.message ??
          'Failed to load courses';
        state.coursesError = errorMessage;
      })
      .addCase(asyncThunks.getCourseTags.pending, state => {
        state.areTagsLoading = true;
      })
      .addCase(asyncThunks.getCourseTags.fulfilled, (state, action) => {
        const apiData = action.payload;
        if (apiData?.tags) {
          state.availableTagsWithTranslations = apiData.tags;
        }

        state.areTagsLoading = false;

        if (state.coursesError?.includes('tags') || state.coursesError?.includes('Tags')) {
          state.coursesError = null;
        }
      })
      .addCase(asyncThunks.getCourseTags.rejected, (state, action) => {
        state.areTagsLoading = false;

        const errorMessage = action.error?.message ?? 'Failed to load course tags';

        state.coursesError ??= errorMessage;
      })
      .addCase(asyncThunks.getCourse.fulfilled, (state, action) => {
        const courseId = String(action.payload.courseId);
        const existingCourse = getExistingCourse(state.courses, courseId);
        const courseDetails = action.payload.courseDetails;

        if (existingCourse && courseDetails) {
          const existingCourseIndex = state.courses.indexOf(existingCourse);

          state.courses[existingCourseIndex] = {
            ...existingCourse,
            id: courseId,
            title: courseDetails.title,
            description: courseDetails.description,
            dates: courseDetails.dates,
            meetingLink: courseDetails.meetingLink,
            meetingPlatform: courseDetails.meetingPlatform,
            isFreshStartMachineReadyToClone: courseDetails.isFreshStartMachineReadyToClone,
            whetherTrainerAllowedPreview: courseDetails.whetherTrainerAllowedPreview,
            hasFreshStart: courseDetails.hasFreshStart,
            trainer: courseDetails.trainer,
            isTrainer: courseDetails.isTrainer,
            isParticipant: courseDetails.isParticipant,
            isTest: courseDetails.isTest,
            timezone: courseDetails.timezone,
            flags: courseDetails.flags,
            organizationName: courseDetails.organizationName,
            machineIframeSize: courseDetails.machineIframeSize,
            joinCode: courseDetails.joinCode
          };
        } else if (courseDetails) {
          state.courses ??= [];
          state.courses.push({
            ...courseDetails,
            id: courseId,
            createdBy: '',
            createdDate: new Date().toISOString()
          } as CourseModel);
        }
      })
      .addCase(asyncThunks.getCourseJoinCode.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg, [{ key: 'isJoinCodeLoading', value: true }]);
      })
      .addCase(asyncThunks.getCourseJoinCode.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'joinCode', value: action.payload.joinCode },
          { key: 'isJoinCodeLoading', value: false },
          { key: 'isJoinCodeLoaded', value: true }
        ]);
      })
      .addCase(asyncThunks.getCourseJoinCode.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg, [{ key: 'isJoinCodeLoading', value: false }]);
      })
      .addCase(asyncThunks.getCourseForEdit.pending, state => {
        state.isCourseForEditLoading = true;
      })
      .addCase(asyncThunks.getCourseForEdit.fulfilled, (state, action) => {
        state.isCourseForEditLoading = false;
        state.courseForEditError = null;
        const courseId = String(action.payload.courseId);
        const existingCourse = getExistingCourse(state.courses, courseId);
        const courseForEdit = action.payload.courseForEdit;

        if (courseForEdit) {
          const coursePayload = {
            id: courseId,
            title: courseForEdit.title,
            description: courseForEdit.description,
            dates: courseForEdit.dates,
            timezone: courseForEdit.timezone,
            maxParticipants: courseForEdit.maxParticipants,
            expectedParticipants: courseForEdit.expectedParticipants,
            organizationId: courseForEdit.daOrganizationId,
            dataCenterIds: courseForEdit.daDataCenterIds?.map(id => id.toString()) ?? [],
            isDemo: courseForEdit.isDemo,
            wantToBeTrainer: courseForEdit.wantToBeTrainer,
            wantToBeCoordinator: courseForEdit.wantToBeCoordinator,
            // Default values for fields not provided by getCourseForEdit endpoint
            joinCode: '',
            machineIframeSize: 4,
            hasFreshStart: false,
            isTrainer: courseForEdit.wantToBeTrainer,
            isParticipant: false,
            isTest: false,
            status: 0,
            userRoles: [],
            meetingLink: undefined,
            meetingPlatform: undefined,
            trainer: undefined,
            organizationName: undefined,
            participantCount: 0,
            activeParticipantCount: 0,
            datacenterId: undefined,
            // Loading states
            isMyDesktopLoading: false,
            isTrainerDesktopLoading: false,
            isFreshStartDesktopLoading: false,
            areParticipantsLoading: false,
            isJoinCodeLoading: false,
            isMyDesktopStartPending: false,
            isTrainerDesktopStartPending: false,
            isFreshStartDesktopStartPending: false,
            isMyDesktopStopPending: false,
            isFreshStartDesktopStopPending: false,
            isMyDesktopDeletePending: false,
            isFreshStartDesktopDeletePending: false,
            isJoinCodeLoaded: false,
            isMyDesktopLoaded: false,
            isTrainerDesktopLoaded: false,
            isFreshStartDesktopLoaded: false,
            areParticipantsLoaded: false,
            // Optional flags
            whetherTrainerAllowedPreview: false,
            isFreshStartMachineReadyToClone: false,
            showOnlyActiveMachines: false,
            participantsMachineControlActive: false
          };

          if (existingCourse) {
            const existingCourseIndex = state.courses.indexOf(existingCourse);
            state.courses[existingCourseIndex] = {
              ...existingCourse,
              ...coursePayload
            };
          } else {
            state.courses ??= [];
            state.courses.push({
              ...coursePayload,
              createdBy: '',
              createdDate: new Date().toISOString()
            } as CourseModel);
          }
        }
      })
      .addCase(asyncThunks.getCourseForEdit.rejected, (state, action) => {
        state.isCourseForEditLoading = false;
        state.courseForEditError = action.error?.message ?? 'Failed to load course for editing';
      })

      .addCase(asyncThunks.getMyDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isMyDesktopLoading', value: true }
        ]);
      })
      .addCase(asyncThunks.getMyDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'myDesktop', value: action.payload.myDesktop },
          { key: 'isMyDesktopLoading', value: false },
          { key: 'isMyDesktopLoaded', value: true }
        ]);
      })
      .addCase(asyncThunks.getMyDesktop.rejected, (state, action) => {
        if ((action.payload as AbortedRequestPayload)?.type === 'aborted') {
          return;
        }

        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isMyDesktopLoading', value: false }
        ]);
      })
      .addCase(asyncThunks.getTrainerDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isTrainerDesktopLoading', value: true }
        ]);
      })
      .addCase(asyncThunks.getTrainerDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'trainerDesktop', value: action.payload.trainerDesktop },
          { key: 'isTrainerDesktopLoading', value: false },
          { key: 'isTrainerDesktopLoaded', value: true }
        ]);
      })
      .addCase(asyncThunks.getTrainerDesktop.rejected, (state, action) => {
        if ((action.payload as AbortedRequestPayload)?.type === 'aborted') {
          return;
        }

        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isTrainerDesktopLoading', value: false }
        ]);
      })
      .addCase(asyncThunks.getFreshStartDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isFreshStartDesktopLoading', value: true }
        ]);
      })
      .addCase(asyncThunks.getFreshStartDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'freshStartDesktop', value: action.payload.freshStartDesktop },
          { key: 'isFreshStartDesktopLoading', value: false },
          { key: 'isFreshStartDesktopLoaded', value: true }
        ]);
      })
      .addCase(asyncThunks.getFreshStartDesktop.rejected, (state, action) => {
        if ((action.payload as any)?.type === 'aborted') {
          return;
        }

        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isFreshStartDesktopLoading', value: false }
        ]);
      })
      .addCase(asyncThunks.getCourseDesktops.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isMyDesktopLoading', value: true },
          { key: 'isTrainerDesktopLoading', value: true }
        ]);
      })
      .addCase(asyncThunks.getCourseDesktops.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'myDesktop', value: action.payload.myDesktop },
          { key: 'trainerDesktop', value: action.payload.trainerDesktop },
          { key: 'isMyDesktopLoading', value: false },
          { key: 'isTrainerDesktopLoading', value: false }
        ]);
      })
      .addCase(asyncThunks.getCourseDesktops.rejected, (state, action) => {
        if ((action.payload as AbortedRequestPayload)?.type === 'aborted') {
          return;
        }

        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isMyDesktopLoading', value: false },
          { key: 'isTrainerDesktopLoading', value: false }
        ]);
      })
      .addCase(asyncThunks.getCoursesMyDesktops.fulfilled, (state, action) => {
        if (action.payload?.myDesktops) {
          for (const courseDesktop of action.payload.myDesktops as any[]) {
            changeCourseValues(state, courseDesktop.courseId, [
              { key: 'myDesktop', value: courseDesktop.myDesktop }
            ]);
          }
        }
      })
      .addCase(asyncThunks.getCoursesTrainerDesktops.fulfilled, (state, action) => {
        if (action.payload?.trainerDesktops) {
          for (const courseDesktop of action.payload.trainerDesktops as any[]) {
            changeCourseValues(state, courseDesktop.courseId, [
              { key: 'trainerDesktop', value: courseDesktop.trainerDesktop }
            ]);
          }
        }
      })
      .addCase(asyncThunks.getParticipants.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg, [
          { key: 'areParticipantsLoading', value: true }
        ]);
      })
      .addCase(asyncThunks.getParticipants.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'participants', value: action.payload.participants },
          { key: 'areParticipantsLoading', value: false },
          { key: 'areParticipantsLoaded', value: true }
        ]);
      })
      .addCase(asyncThunks.getParticipants.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg, [
          { key: 'areParticipantsLoading', value: false }
        ]);
      })
      .addCase(asyncThunks.saveIsFreshStartMachineReadyToClone.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          {
            key: 'isFreshStartMachineReadyToClone',
            value: action.payload.isFreshStartMachineReadyToClone
          }
        ]);
      })
      .addCase(asyncThunks.saveWhetherTrainerAllowedPreview.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          {
            key: 'whetherTrainerAllowedPreview',
            value: action.payload.whetherTrainerAllowedPreview
          }
        ]);
      })
      .addCase(asyncThunks.saveShowOnlyActiveMachines.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          {
            key: 'showOnlyActiveMachines',
            value: action.payload.showOnlyActiveMachines
          }
        ]);
      })
      .addCase(asyncThunks.saveParticipantsMachineControlActive.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          {
            key: 'participantsMachineControlActive',
            value: action.payload.participantsMachineControlActive
          }
        ]);
      })
      .addCase(asyncThunks.saveMachineIframeSize.fulfilled, (state, action) => {
        if (action.payload?.courseId && action.payload?.machineIframeSize !== undefined) {
          changeCourseValues(state, action.payload.courseId, [
            {
              key: 'machineIframeSize',
              value: action.payload.machineIframeSize
            }
          ]);
        }
      })
      .addCase(asyncThunks.saveMachineIframeSize.rejected, (state, action) => {
        console.error('Failed to save machine iframe size:', action.error.message);
      })
      .addCase(asyncThunks.createFreshStartFromOsTemplate.pending, state => {
        state.isFreshStartCreating = true;
      })
      .addCase(asyncThunks.createFreshStartFromOsTemplate.fulfilled, (state, action) => {
        state.isFreshStartCreating = false;
        setHasFreshStart(state, action.payload.courseId as string, true);
      })
      .addCase(asyncThunks.createFreshStartFromOsTemplate.rejected, state => {
        state.isFreshStartCreating = false;
      })
      .addCase(asyncThunks.createFreshStartFromStandaloneTemplate.pending, state => {
        state.isFreshStartCreating = true;
      })
      .addCase(asyncThunks.createFreshStartFromStandaloneTemplate.fulfilled, (state, action) => {
        state.isFreshStartCreating = false;
        setHasFreshStart(state, action.payload.courseId as string, true);
      })
      .addCase(asyncThunks.createFreshStartFromStandaloneTemplate.rejected, state => {
        state.isFreshStartCreating = false;
      })
      .addCase(asyncThunks.deleteParticipantDesktop.pending, (state, action) => {
        changeParticipantValues(state, action.meta.arg.courseId, action.meta.arg.participantId, [
          { key: 'isDesktopDeletePending', value: true }
        ]);
      })
      .addCase(asyncThunks.deleteParticipantDesktop.fulfilled, (state, action) => {
        changeParticipantValues(state, action.payload.courseId, action.payload.participantId, [
          { key: 'isDesktopDeletePending', value: false },
          { key: 'desktop', value: null }
        ]);
      })
      .addCase(asyncThunks.deleteParticipantDesktop.rejected, (state, action) => {
        changeParticipantValues(state, action.meta.arg.courseId, action.meta.arg.participantId, [
          { key: 'isDesktopDeletePending', value: false }
        ]);
      })
      .addCase(asyncThunks.deleteMyDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId as string, [
          { key: 'isMyDesktopDeletePending', value: true }
        ]);
      })
      .addCase(asyncThunks.deleteMyDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'isMyDesktopDeletePending', value: false },
          { key: 'myDesktop', value: null }
        ]);
      })
      .addCase(asyncThunks.deleteMyDesktop.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId as string, [
          { key: 'isMyDesktopDeletePending', value: false }
        ]);
      })
      .addCase(asyncThunks.deleteFreshStartDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId as string, [
          { key: 'isFreshStartDesktopDeletePending', value: true }
        ]);

        const existingCourse = getExistingCourse(state.courses, action.meta.arg.courseId as string);

        if (existingCourse?.freshStartDesktop) {
          existingCourse.freshStartDesktop.isRunning = false;
        }
      })
      .addCase(asyncThunks.deleteFreshStartDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'isFreshStartDesktopDeletePending', value: false },
          { key: 'freshStartDesktop', value: null },
          {
            key: 'hasFreshStart',
            value: false
          },
          {
            key: 'isFreshStartMachineReadyToClone',
            value: false
          }
        ]);
      })
      .addCase(asyncThunks.deleteFreshStartDesktop.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId as string, [
          { key: 'isFreshStartDesktopDeletePending', value: false }
        ]);
      })
      .addCase(asyncThunks.stopParticipantDesktop.pending, (state, action) => {
        changeParticipantValues(state, action.meta.arg.courseId, action.meta.arg.participantId, [
          { key: 'isDesktopStopPending', value: true }
        ]);

        const existingCourse = getExistingCourse(state.courses, action.meta.arg.courseId);

        if (existingCourse) {
          const existingParticipant = existingCourse.participants?.find(
            p => p.id === action.meta.arg.participantId
          );

          if (existingParticipant?.desktop) {
            existingParticipant.desktop.isRunning = false;
          }
        }
      })
      .addCase(asyncThunks.stopParticipantDesktop.fulfilled, (state, action) => {
        changeParticipantValues(state, action.payload.courseId, action.payload.participantId, [
          { key: 'isDesktopStopPending', value: false }
        ]);
      })
      .addCase(asyncThunks.stopParticipantDesktop.rejected, (state, action) => {
        changeParticipantValues(state, action.meta.arg.courseId, action.meta.arg.participantId, [
          { key: 'isDesktopStopPending', value: false }
        ]);
      })
      .addCase(asyncThunks.stopMyDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isMyDesktopStopPending', value: true }
        ]);

        const existingCourse = getExistingCourse(state.courses, action.meta.arg.courseId);

        if (existingCourse?.myDesktop) {
          existingCourse.myDesktop.isRunning = false;
        }
      })
      .addCase(asyncThunks.stopMyDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'isMyDesktopStopPending', value: false }
        ]);
      })
      .addCase(asyncThunks.stopMyDesktop.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isMyDesktopStopPending', value: false }
        ]);
      })
      .addCase(asyncThunks.stopTrainerDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isTrainerDesktopStopPending', value: true }
        ]);

        const existingCourse = getExistingCourse(state.courses, action.meta.arg.courseId);

        if (existingCourse?.trainerDesktop) {
          existingCourse.trainerDesktop.isRunning = false;
        }
      })
      .addCase(asyncThunks.stopTrainerDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'isTrainerDesktopStopPending', value: false }
        ]);
      })
      .addCase(asyncThunks.stopTrainerDesktop.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isTrainerDesktopStopPending', value: false }
        ]);
      })
      .addCase(asyncThunks.stopFreshStartDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isFreshStartDesktopStopPending', value: true }
        ]);

        const existingCourse = getExistingCourse(state.courses, action.meta.arg.courseId);

        if (existingCourse?.freshStartDesktop) {
          existingCourse.freshStartDesktop.isRunning = false;
        }
      })
      .addCase(asyncThunks.stopFreshStartDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'isFreshStartDesktopStopPending', value: false }
        ]);
      })
      .addCase(asyncThunks.stopFreshStartDesktop.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg.courseId, [
          { key: 'isFreshStartDesktopStopPending', value: false }
        ]);
      })
      .addCase(asyncThunks.connectToMyDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg, [
          { key: 'isMyDesktopStartPending', value: true }
        ]);
      })
      .addCase(asyncThunks.connectToMyDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'myDesktop', value: action.payload.myDesktop },
          { key: 'isMyDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.connectToMyDesktop.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg, [
          { key: 'isMyDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.connectToFreshStartDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg, [
          { key: 'isFreshStartDesktopStartPending', value: true }
        ]);
      })
      .addCase(asyncThunks.connectToFreshStartDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'freshStartDesktop', value: action.payload.freshStartDesktop },
          { key: 'isFreshStartDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.connectToFreshStartDesktop.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg, [
          { key: 'isFreshStartDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.connectToTrainerDesktop.pending, (state, action) => {
        changeCourseValues(state, action.meta.arg, [
          { key: 'isTrainerDesktopStartPending', value: true }
        ]);
      })
      .addCase(asyncThunks.connectToTrainerDesktop.fulfilled, (state, action) => {
        changeCourseValues(state, action.payload.courseId, [
          { key: 'trainerDesktop', value: action.payload.trainerDesktop },
          { key: 'isTrainerDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.connectToTrainerDesktop.rejected, (state, action) => {
        changeCourseValues(state, action.meta.arg, [
          { key: 'isTrainerDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.connectToParticipantDesktop.pending, (state, action) => {
        changeParticipantValues(state, action.meta.arg.courseId, action.meta.arg.participantId, [
          { key: 'isDesktopStartPending', value: true }
        ]);
      })
      .addCase(asyncThunks.connectToParticipantDesktop.fulfilled, (state, action) => {
        changeParticipantValues(state, action.payload.courseId, action.payload.participantId, [
          { key: 'desktop', value: action.payload.participantDesktop },
          { key: 'isDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.connectToParticipantDesktop.rejected, (state, action) => {
        changeParticipantValues(state, action.meta.arg.courseId, action.meta.arg.participantId, [
          { key: 'isDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.startParticipantDesktop.pending, (state, action) => {
        changeParticipantValues(state, action.meta.arg.courseId, action.meta.arg.participantId, [
          { key: 'isDesktopStartPending', value: true }
        ]);
      })
      .addCase(asyncThunks.startParticipantDesktop.fulfilled, (state, action) => {
        changeParticipantValues(state, action.payload.courseId, action.payload.participantId, [
          { key: 'desktop', value: action.payload.participantDesktop },
          { key: 'isDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.startParticipantDesktop.rejected, (state, action) => {
        changeParticipantValues(state, action.meta.arg.courseId, action.meta.arg.participantId, [
          { key: 'isDesktopStartPending', value: false }
        ]);
      })
      .addCase(asyncThunks.createCourse.pending, state => {
        state.isCourseCreating = true;
        state.isCreateCourseSuccess = false;
        state.createCourseResult = null;
      })
      .addCase(asyncThunks.createCourse.fulfilled, (state, action) => {
        state.isCourseCreating = false;
        state.isCreateCourseSuccess = true;
        state.createCourseResult = action.payload;

        if (action.payload) {
          const newCourse: CourseModel = {
            id: action.payload.id,
            title: action.payload.title,
            description: action.payload.description,
            createdBy: '',
            createdDate: new Date().toISOString(),
            joinCode: action.payload.joinCode,
            machineIframeSize: action.payload.machineIframeSize,
            hasFreshStart: action.payload.hasFreshStart,
            isTrainer: action.payload.isTrainer,
            isParticipant: action.payload.isParticipant,
            isTest: action.payload.isTest,
            isDemo: action.payload.flags?.isDemo ?? false,
            status: 0,
            userRoles: [1],
            dates: action.payload.dates,
            timezone: action.payload.timezone,
            flags: action.payload.flags,
            myDesktop: null,
            trainerDesktop: null,
            freshStartDesktop: null,
            participants: []
          };

          state.courses = [newCourse, ...state.courses];
        }
      })
      .addCase(asyncThunks.createCourse.rejected, state => {
        state.isCourseCreating = false;
        state.isCreateCourseSuccess = false;
      })
      .addCase(asyncThunks.updateCourse.pending, state => {
        state.isCourseUpdating = true;
        state.isUpdateCourseSuccess = false;
        state.updateCourseResult = null;
      })
      .addCase(asyncThunks.updateCourse.fulfilled, (state, action) => {
        state.isCourseUpdating = false;
        state.isUpdateCourseSuccess = true;
        state.updateCourseResult = action.payload;
      })
      .addCase(asyncThunks.updateCourse.rejected, state => {
        state.isCourseUpdating = false;
        state.isUpdateCourseSuccess = false;
      })
      .addCase(asyncThunks.getStandalones.pending, state => {
        state.areStandalonesLoading = true;
      })
      .addCase(asyncThunks.getStandalones.fulfilled, (state, action) => {
        state.standalones = action.payload.standalones ?? [];
        state.areStandalonesLoading = false;
      })
      .addCase(asyncThunks.getStandalones.rejected, state => {
        state.areStandalonesLoading = false;
      })
      .addCase(asyncThunks.getFreshStartServersForCourse.pending, (state, action) => {
        const courseId = action.meta.arg.courseId;
        state.areFreshStartServersLoading = state.areFreshStartServersLoading || {};
        state.areFreshStartServersLoading[courseId] = true;
      })
      .addCase(asyncThunks.getFreshStartServersForCourse.fulfilled, (state, action) => {
        const courseId = action.payload.courseId;
        state.freshStartServers = state.freshStartServers || {};
        state.areFreshStartServersLoading = state.areFreshStartServersLoading || {};
        state.freshStartServers[courseId] = action.payload.servers;
        state.areFreshStartServersLoading[courseId] = false;
      })
      .addCase(asyncThunks.getFreshStartServersForCourse.rejected, (state, action) => {
        const courseId = action.meta.arg.courseId;
        state.areFreshStartServersLoading = state.areFreshStartServersLoading || {};
        state.areFreshStartServersLoading[courseId] = false;
      })
      .addCase(asyncThunks.getOsTemplatesOfServer.pending, (state, action) => {
        const serverName = action.meta.arg.serverName;
        state.areOsTemplatesLoading = state.areOsTemplatesLoading || {};
        state.osTemplatesErrors = state.osTemplatesErrors ?? {};
        state.areOsTemplatesLoading[serverName] = true;
        state.osTemplatesErrors[serverName] = false;
      })
      .addCase(asyncThunks.getOsTemplatesOfServer.fulfilled, (state, action) => {
        const serverName = action.payload.serverName;
        state.osTemplates = state.osTemplates || {};
        state.areOsTemplatesLoading = state.areOsTemplatesLoading || {};
        state.osTemplatesErrors = state.osTemplatesErrors ?? {};
        state.osTemplates[serverName] = action.payload.templates;
        state.areOsTemplatesLoading[serverName] = false;
        state.osTemplatesErrors[serverName] = false;
      })
      .addCase(asyncThunks.getOsTemplatesOfServer.rejected, (state, action) => {
        const serverName = action.meta.arg.serverName;
        state.areOsTemplatesLoading = state.areOsTemplatesLoading || {};
        state.osTemplatesErrors = state.osTemplatesErrors ?? {};
        state.areOsTemplatesLoading[serverName] = false;
        state.osTemplatesErrors[serverName] = true;
      });
  }
});

export const {
  clearCourses,
  addParticipant,
  newDesktopConnected,
  courseTrainerAllowedToPreview,
  isFreshStartMachineReadyToClone,
  setCourseListViewMode,
  clearCreateCourseResult,
  clearUpdateCourseResult,
  clearCourseForEditError,
  resetCoursesError,
  setCoursesLoaded,
  clearOsTemplatesError,
  resetCourseDataLoadedStates,
  resetDesktopLoadingStates
} = coursesSlice.actions;

export const coursesReducer = coursesSlice.reducer;
